<template>
    <div>
        <ve-table rowKeyFieldName="rowKey" :fixed-header="true" :columns="computedHeaders" :table-data="data"
            :edit-option="readonly ? undefined : editOption" :rowStyleOption="rowStyleOption"
            :clipboard-option="clipboardOption"
            :column-width-resize-option="columnWidthResizeOption" border-y :cell-style-option="cellStyleOption"
            :max-height="height" :scroll-width="totalColumnsWidth" style="word-break:break-word"/>
    </div>
</template>

<script>
import { h } from 'vue';
import SearchableSelect from "./SearchableSelect.vue";

export default {
    name: "DealTable",
    props: ["headers", "data", "height", "type", "showActionAcc", "userPermission", "efficFunc", "respList", "updateStatusFunc", "processList", "readonly", "originList", "manageOriginFunc", "mainCauseList"],
    data() {
        return {
            rowToDelete: null,
            dataSaved: [...this.data],
            columnWidthResizeOption: {
                enable: true,
                minWidth: 60
            },
            rowStyleOption: {
                clickHighlight: false,
                hoverHighlight: false,
            },
            editOption: {
                beforeCellValueChange: () => { },
                afterCellValueChange: ({ row, column }) => {
                    if (!column || column.field !== "mainCause") return;
                    if (this.type !== "5pq" && this.type !== "ishik") return;
                    const origin = this.type === "5pq" ? "5Pqs" : "6Ms";
                    this.$emit("mainCauseBlur", { row, origin });
                },
            },
            cellStyleOption: {
                headerCellClass: ({ column, rowIndex }) => {
                    const classes = [];
                    if (rowIndex == 0) classes.push("deal-header-table");
                    if (column && column.required) classes.push("deal-header-required");
                    return classes.join(" ");
                },
                bodyCellClass: ({ row, column }) => {
                    if (row && row._flashAt) return "rta-flash-yellow";
                    if (column.field == "actionDelete") return "deal-header-table-delete-class";
                    return "";
                }
            },
        };
    },
    watch: {
        data: {
            handler(newValue) {
                this.dataSaved = [...newValue];
            },
            deep: true,
            immediate: true,
        },
    },
    computed: {
        // Colagem com a célula apenas SELECIONADA (clique simples + Ctrl+V) usa o caminho
        // de clipboard do vue-easytable, que grava o valor mas NÃO dispara afterCellValueChange.
        // Aqui reemitimos mainCauseBlur APENAS para as linhas cuja coluna mainCause foi colada,
        // reaproveitando a lógica de criação/sincronização da linha vinculada no Plano de Ação
        // (idempotente por linha). Escopar à linha colada evita ressuscitar ações deletadas e
        // flash em massa — response.data traz só os campos das colunas que entraram no range.
        clipboardOption() {
            return {
                afterPaste: (response) => {
                    if (this.type !== "5pq" && this.type !== "ishik") return;
                    if (!response || !response.selectionRangeIndexes || !Array.isArray(response.data)) return;

                    const origin = this.type === "5pq" ? "5Pqs" : "6Ms";
                    const { startRowIndex } = response.selectionRangeIndexes;

                    response.data.forEach((pastedRow, offset) => {
                        // só reprocessa se a Causa Fundamental foi colada nesta linha
                        if (!pastedRow || !("mainCause" in pastedRow)) return;
                        const row = this.data[startRowIndex + offset];
                        if (row) this.$emit("mainCauseBlur", { row, origin });
                    });
                },
            };
        },
        totalColumnsWidth() {
            return this.computedHeaders.reduce((sum, col) => sum + (col.width || 100), 0);
        },
        computedHeaders() {
            const typeOptions = [
                { label: "Obrigatório", value: "obrigatorio" },
                { label: "Opcional", value: "opcional" },
            ];

            const sixmOptions = [
                { label: "Método", value: "metodo" },
                { label: "Máquina", value: "maquina" },
                { label: "Medida", value: "medida" },
                { label: "Meio Ambiente", value: "meioambiente" },
                { label: "Mão de Obra", value: "maodeobra" },
                { label: "Matéria Prima", value: "materiaprima" },
            ];

            const originOptions = [
                { label: "Mecânica", value: "Mecânica" },
                { label: "Elétrica", value: "Elétrica" },
                { label: "Instrumentação", value: "Instrumentação" },
                { label: "Operacional", value: "Operacional" },
                { label: "Matéria Prima", value: "Matéria Prima" },
            ];

            // Mapeamento de status para exibição (campo bloqueado - calculado automaticamente)
            const statusLabels = {
                "not-started":       "Não Iniciado",
                "in-progress":       "Em Andamento",
                "delayed":           "Em Atraso",
                "completed":         "Concluído",
                "completed-late":    "Concluído com atraso",
                "evaluation-pending":"Avaliação Pendente",
                "pending":           "Pendente",
                "finished":          "Finalizado"
            };

            // Filtra a coluna Eficácia quando avaliação não é "Por Ação" (valor 0)
            let filteredHeaders = this.showActionAcc != 0
                ? this.headers.filter(col => col.field !== "actionAcc")
                : [...this.headers];

            // Esconde a coluna Remover quando readonly (RTA concluída)
            if (this.readonly) {
                filteredHeaders = filteredHeaders.filter(col => col.field !== "actionDelete");
            }

            return filteredHeaders.map((col) => {
                if (col.field === "mainCause" && (this.type === "5pq" || this.type === "ishik")) {
                    return col;
                }

                if (col.field === "mainReason") {
                    return {
                        ...col,
                        ellipsis: null,
                        renderBodyCell: ({ row }) => h(SearchableSelect, {
                            props: {
                                modelRef: row[col.field],
                                optList: this.mainCauseList || [],
                                optValue: "value",
                                optDisplay: "label",
                                badgeKey: "origin",
                                inputFunc: (val) => { row[col.field] = val.target.value; },
                                placeholder: "Causa Fundamental",
                                disabled: !!this.readonly,
                                maxDisplayChars: 35
                            },
                            class: "table-inline-select"
                        })
                    };
                }

                if (["whenInitPrev", "whenFinishPrev", "whenInitReal", "whenFinishReal"].includes(col.field)) {
                    const isFinishReal = col.field === "whenFinishReal";
                    const todayStr = new Date().toISOString().slice(0, 10);

                    return {
                        ...col,
                        renderBodyCell: ({ row }) => {
                            const isSaved = row._savedDates && row._savedDates[col.field];
                            return (isSaved || this.readonly)
                                ? (
                                    <input
                                        type="date"
                                        value={row[col.field]}
                                        readonly
                                        disabled
                                        style="padding:0; border:none; text-align:center; background:none; max-width:6rem; color:#333; cursor:default;"
                                    ></input>
                                )
                                : (
                                    <input
                                        size="small"
                                        value={row[col.field]}
                                        on-input={(val) => {
                                            let selectedDate = val.target.value;
                                            if (isFinishReal && selectedDate > todayStr) {
                                                this.$bvToast.toast(
                                                    'A data de finalização real não pode ser maior que hoje. O dia de hoje foi selecionado automaticamente.',
                                                    {
                                                        title: 'Data inválida',
                                                        variant: 'warning',
                                                        toaster: 'b-toaster-bottom-left',
                                                        solid: true,
                                                        autoHideDelay: 4000,
                                                    }
                                                );
                                                selectedDate = todayStr;
                                                val.target.value = todayStr;
                                            }
                                            row[col.field] = selectedDate;
                                            // Recalcula o status quando qualquer data é alterada
                                            if (this.updateStatusFunc && typeof this.updateStatusFunc === 'function') {
                                                row.actionStatus = this.updateStatusFunc(row);
                                            }
                                        }}
                                        title={row[col.field]}
                                        type="date"
                                        max={isFinishReal ? todayStr : undefined}
                                        value-format="yyyy-MM-dd"
                                        style="padding:0; border:none; text-align:center; background:none; max-width:6rem"
                                    ></input>
                                );
                        }
                    };
                }

                if (col.field === "actionType") {
                    return {
                        ...col,
                        ellipsis: null,
                        renderBodyCell: ({ row }) => {
                            return h(SearchableSelect, {
                                props: {
                                    modelRef: row[col.field],
                                    optList: typeOptions,
                                    optValue: "value",
                                    optDisplay: "label",
                                    inputFunc: (val) => { row[col.field] = val.target.value; },
                                    placeholder: "Tipo",
                                    disabled: !!this.readonly
                                },
                                class: "table-inline-select table-narrow-select"
                            });
                        }
                    };
                }

                if (col.field === "sixm") {
                    return {
                        ...col,
                        ellipsis: null,
                        renderBodyCell: ({ row }) => {
                            return h(SearchableSelect, {
                                props: {
                                    modelRef: row[col.field],
                                    optList: sixmOptions,
                                    optValue: "value",
                                    optDisplay: "label",
                                    inputFunc: (val) => { row[col.field] = val.target.value; },
                                    placeholder: "6Ms",
                                    disabled: !!this.readonly
                                },
                                class: "table-inline-select"
                            });
                        }
                    };
                }

                if (col.field === "origin") {
                    const effectiveOriginOptions = this.originList && this.originList.length
                        ? this.originList
                        : originOptions;

                    const colDef = {
                        ...col,
                        ellipsis: null,
                        renderBodyCell: ({ row }) => {
                            return h(SearchableSelect, {
                                props: {
                                    modelRef: row[col.field],
                                    optList: effectiveOriginOptions,
                                    optValue: "value",
                                    optDisplay: "label",
                                    inputFunc: (val) => { row[col.field] = val.target.value; },
                                    placeholder: "Origem",
                                    disabled: !!this.readonly
                                },
                                class: "table-inline-select table-narrow-select"
                            });
                        }
                    };

                    if (this.manageOriginFunc) {
                        colDef.renderHeaderCell = ({ column }) => {
                            return h("div", { style: "display:flex; align-items:center; gap:4px; justify-content:center;" }, [
                                h("span", {}, column.title),
                                h("button", {
                                    class: "btn-manage-origin",
                                    attrs: { title: "Gerenciar opções de Origem", type: "button" },
                                    on: { click: () => this.manageOriginFunc() }
                                }, [
                                    h("svg", {
                                        attrs: {
                                            xmlns: "http://www.w3.org/2000/svg",
                                            width: "13",
                                            height: "13",
                                            fill: "currentColor",
                                            viewBox: "0 0 16 16"
                                        },
                                        domProps: {
                                            innerHTML: `<path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.499.499 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11z"/>`
                                        }
                                    })
                                ])
                            ]);
                        };
                    }

                    return colDef;
                }

                if (col.field === "who") {
                    return {
                        ...col,
                        ellipsis: null,
                        renderBodyCell: ({ row }) => {
                            return h(SearchableSelect, {
                                props: {
                                    modelRef: row[col.field],
                                    optList: this.respList || [],
                                    optValue: "id",
                                    optDisplay: "name",
                                    inputFunc: (val) => { row[col.field] = val.target.value; },
                                    placeholder: "Responsável",
                                    disabled: !!this.readonly
                                },
                                class: "table-inline-select table-narrow-select"
                            });
                        }
                    };
                }

                if (col.field === "where") {
                    return {
                        ...col,
                        ellipsis: null,
                        renderBodyCell: ({ row }) => {
                            return h(SearchableSelect, {
                                props: {
                                    modelRef: row[col.field],
                                    optList: this.processList || [],
                                    optValue: "id",
                                    optDisplay: "description",
                                    inputFunc: (val) => { row[col.field] = val.target.value; },
                                    placeholder: "Processo",
                                    disabled: !!this.readonly
                                },
                                class: "table-inline-select table-narrow-select"
                            });
                        }
                    };
                }

                if (col.field === "actionStatus") {
                    return {
                        ...col,
                        renderBodyCell: ({ row }) => {
                            // Status é calculado automaticamente e não pode ser editado
                            const statusLabel = statusLabels[row[col.field]] || row[col.field] || "Não Iniciado";

                            // Define cores baseadas no status
                            let statusColor = "#666";
                            if (row[col.field] === "delayed")            statusColor = "#d32f2f";
                            else if (row[col.field] === "in-progress")   statusColor = "#1976d2";
                            else if (row[col.field] === "completed")     statusColor = "#388e3c";
                            else if (row[col.field] === "completed-late")statusColor = "#FF5F00";
                            else if (row[col.field] === "evaluation-pending") statusColor = "#7B1FA2";
                            else if (row[col.field] === "not-started")   statusColor = "#757575";

                            const evalData = row.evaluationData;
                            const evalTag = evalData
                                ? h("span", { style: "font-size:0.75rem; display:block;" },
                                    `(${evalData.approveEfficiency ? 'Aprovado' : 'Reprovado'})`)
                                : null;

                            return h("div", {
                                style: "text-align:center; line-height:1.3",
                                title: "Status calculado automaticamente"
                            }, [
                                h("span", { style: `color: ${statusColor}; font-weight: 500;` }, statusLabel),
                                evalTag
                            ]);
                        }
                    };
                }

                if (col.field === "actionAcc" && this.showActionAcc != undefined && this.showActionAcc == 0) {
                    return {
                        ...col,
                        renderBodyCell: ({ row }) => {
                            const canEval = ['evaluation-pending', 'completed', 'completed-late'].includes(row.actionStatus);
                            const canAvaliate = this.userPermission && this.userPermission.canAvaliate;
                            const label = row.evaluationData ? 'Avaliado' : 'Avaliar';
                            const isDisabled = !canEval || !canAvaliate;
                            const tooltipTitle = !canEval
                                ? 'Para avaliar esta ação, cadastre a data de inicio e termino real'
                                : '';
                            return h(
                                "button",
                                {
                                    class: "avaliationBtn",
                                    style: isDisabled
                                        ? "font-size:0.72rem; opacity:0.5; cursor:not-allowed;"
                                        : "cursor:pointer; font-size:0.72rem;",
                                    attrs: { disabled: isDisabled, title: tooltipTitle },
                                    on: {
                                        click: () => {
                                            if (!isDisabled) this.efficFunc('action', row.contextId);
                                        }
                                    }
                                },
                                label
                            );
                        }
                    };
                }

                if (col.field === "actionDelete") {
                    return {
                        ...col,
                        renderBodyCell: ({ row }) => {
                            return h(
                                "span",
                                {
                                    style: "cursor: pointer",
                                    on: {
                                        click: () => this.askToDelete(row),
                                    },
                                    title:'Remover'
                                },
                                [
                                    h("svg", {
                                        attrs: {
                                            xmlns: "http://www.w3.org/2000/svg",
                                            width: "16",
                                            height: "16",
                                            fill: "#FA394D",
                                            class: "bi bi-trash-fill",
                                            viewBox: "0 0 16 16",
                                        },
                                        domProps: {
                                            innerHTML: `
                                            <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0" />
                                        `,
                                        },
                                    }),
                                ]
                            );
                        },
                    };
                }
                return col;
            });
        },
    },
    methods: {
        askToDelete(row) {
            this.rowToDelete = {
                data: { ...row },
                type: this.type
            };
            this.$emit('toDeleteItem', this.rowToDelete)

            this.$bvModal.show('rtaDealDelete');
        },

        cellDataChange(row, column, cellValue) {
            const { data } = this;

            let currentCell = data.find(
                (x) => x.rowKey === row["rowKey"] && x.field === column.field,
            );

            if (currentCell) {
                currentCell.value = cellValue;
            } else {
                let newCell = {
                    rowKey: row["rowKey"],
                    field: column.field,
                    value: cellValue,
                };
                this.data.push(newCell);
            }
        },
    },

    mounted() {
        this.dataSaved = this.data;
    },
};
</script>

<style>
.ve-table{
    z-index: 0;
}

.btn-manage-origin {
    background: none;
    border: none;
    padding: 0 2px;
    cursor: pointer;
    color: #555;
    line-height: 1;
    display: inline-flex;
    align-items: center;
}

.btn-manage-origin:hover {
    color: #007bff;
}

.deal-header-table {
    background: #EBEBEB !important;
    color: #000000 !important;
    font-size: 0.8rem !important;
    font-weight: 600 !important;
    padding: 0.1rem !important;
    height: max-content !important;
    vertical-align: middle !important;
    border: 1px solid #8B8B8B !important;
}

.deal-header-table span {
    display: inline-flex !important;
    align-items: center;
}

.deal-header-table span i:first-child {
    top: -3px !important;
}

.deal-header-table span i:last-child {
    top: 6.5px !important;
}

.deal-header-table-delete-class {
    background-color: #ffefef !important;
}

.deal-header-required .ve-table-header-th-title::after,
.deal-header-required > span::after,
.deal-header-required::after {
    content: " *";
    color: #FF5F00;
    font-weight: 700;
    margin-left: 2px;
}

.rta-flash-yellow {
    animation: rta-flash-yellow 2s cubic-bezier(0.22, 0.61, 0.36, 1) 1;
    background-color: transparent;
    will-change: background-color, box-shadow;
}

@keyframes rta-flash-yellow {
    0%   { background-color: #FFEB3B; box-shadow: inset 0 0 0 1px rgba(255, 193, 7, 0.55); }
    12%  { background-color: #FFF176; box-shadow: inset 0 0 0 1px rgba(255, 213, 79, 0.45); }
    45%  { background-color: #FFF59D; box-shadow: inset 0 0 0 1px rgba(255, 213, 79, 0.25); }
    80%  { background-color: #FFF9C4; box-shadow: inset 0 0 0 1px rgba(255, 213, 79, 0.10); }
    100% { background-color: transparent; box-shadow: inset 0 0 0 1px transparent; }
}

.avaliationBtn{
    border: none;
    border-radius: 0.2rem;
    padding: 0.3rem;
    color: #ffffff;
    font-weight: 500;
    background-color: #008787;
}

.table-inline-select {
    border: none !important;
    padding: 0.1rem !important;
    background: transparent !important;
    min-height: 32px;
}

.table-inline-select .select-shell {
    border: none !important;
    padding: 0.1rem 0.2rem !important;
    height: 32px;
    background: transparent !important;
}

.table-inline-select .select-value {
    min-width: 0;
}

.table-inline-select .arrow {
    flex-shrink: 0;
    min-width: 16px;
    width: 16px;
}


.table-inline-select .select-dropdown {
    min-width: 180px;
}

.ve-table-body-td[col-key="whenInitPrev"],
.ve-table-body-td[col-key="whenFinishPrev"],
.ve-table-body-td[col-key="whenInitReal"],
.ve-table-body-td[col-key="whenFinishReal"],
.ve-table-body-td[col-key="who"],
.ve-table-header-th[col-key="who"],
.ve-table-body-td[col-key="where"],
.ve-table-header-th[col-key="where"],
.ve-table-body-td[col-key="actionType"],
.ve-table-header-th[col-key="actionType"]{
    padding-left: 0 !important;
    padding-right: 0 !important;
}

.ve-table-body-td[col-key="actionStatus"],
.ve-table-header-th[col-key="actionStatus"]{
    padding-left: 0 !important;
    padding-right: 0 !important;
    white-space: normal !important;
    word-break: break-word !important;
}

.ve-table-body-td[col-key="actionDelete"]{
    padding-left: 4px !important;
    padding-right: 4px !important;
}

</style>
